import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Avatar, AvatarFallback} from '@common-web/components/ui/avatar';
import {
  CheckCircle,
  Edit,
  FileText,
  MessageSquare,
  Trash2,
  UserPlus,
  Clock,
  Archive,
  ArrowRight,
} from 'lucide-react';
import {ProspectAction} from '@common/types/prospect';

interface ProspectHistoryItem {
  id: string;
  action: ProspectAction;
  description: string | null;
  oldData?: unknown;
  newData?: unknown;
  changedBy: string | null;
  changedByUser?: {
    id: string;
    name: string;
    email: string;
  } | null;
  createdAt: string;
}

export function ProspectHistory({history}: {history: ProspectHistoryItem[]}) {
  const getActionIcon = (action: ProspectAction) => {
    switch (action) {
      case ProspectAction.CREATED:
        return <FileText className='h-4 w-4 text-blue-500' />;
      case ProspectAction.UPDATED:
        return <Edit className='h-4 w-4 text-blue-500' />;
      case ProspectAction.STAGE_CHANGED:
        return <ArrowRight className='h-4 w-4 text-purple-500' />;
      case ProspectAction.NOTE_ADDED:
        return <MessageSquare className='h-4 w-4 text-green-500' />;
      case ProspectAction.NOTE_UPDATED:
        return <Edit className='h-4 w-4 text-amber-500' />;
      case ProspectAction.NOTE_DELETED:
        return <Trash2 className='h-4 w-4 text-red-500' />;
      case ProspectAction.BROKER_ASSIGNED:
        return <UserPlus className='h-4 w-4 text-indigo-500' />;
      case ProspectAction.PROPOSAL_SAVED:
        return <FileText className='h-4 w-4 text-green-600' />;
      case ProspectAction.PROPOSAL_DELETED:
        return <Trash2 className='h-4 w-4 text-orange-500' />;
      case ProspectAction.CONVERTED_TO_DEAL:
        return <CheckCircle className='h-4 w-4 text-green-700' />;
      case ProspectAction.ARCHIVED:
        return <Archive className='h-4 w-4 text-gray-500' />;
      default:
        return <Clock className='h-4 w-4 text-gray-500' />;
    }
  };

  const getActionDescription = (action: ProspectAction) => {
    switch (action) {
      case ProspectAction.CREATED:
        return 'Prospect Created';
      case ProspectAction.UPDATED:
        return 'Details Updated';
      case ProspectAction.STAGE_CHANGED:
        return 'Stage Changed';
      case ProspectAction.NOTE_ADDED:
        return 'Note Added';
      case ProspectAction.NOTE_UPDATED:
        return 'Note Updated';
      case ProspectAction.NOTE_DELETED:
        return 'Note Deleted';
      case ProspectAction.BROKER_ASSIGNED:
        return 'Broker Assigned';
      case ProspectAction.PROPOSAL_SAVED:
        return 'Proposal Saved';
      case ProspectAction.PROPOSAL_DELETED:
        return 'Proposal Deleted';
      case ProspectAction.CONVERTED_TO_DEAL:
        return 'Converted to Deal';
      case ProspectAction.ARCHIVED:
        return 'Archived';
      default:
        return 'Unknown Action';
    }
  };

  if (history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Audit Trail</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='text-center py-8 text-muted-foreground'>No history available</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Audit Trail ({history.length} entries)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='space-y-4'>
          {history.map((item, index) => (
            <div
              key={item.id}
              className={`flex gap-4 p-4 rounded-lg border ${
                index === 0 ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className='shrink-0'>{getActionIcon(item.action)}</div>
              <div className='grow min-w-0'>
                <div className='flex items-start justify-between gap-4'>
                  <div className='grow'>
                    <div className='flex items-center gap-2 mb-2'>
                      <span className='font-medium text-sm'>
                        {getActionDescription(item.action)}
                      </span>
                    </div>
                    {item.description &&
                      item.action !== ProspectAction.NOTE_UPDATED &&
                      item.action !== ProspectAction.NOTE_DELETED && (
                        <p className='text-sm text-gray-600 mb-2'>{item.description}</p>
                      )}
                    {item.action === ProspectAction.NOTE_UPDATED &&
                      item.oldData &&
                      item.newData && (
                        <div className='flex flex-col gap-2 text-sm mb-2'>
                          <div>
                            <span className='text-xs font-medium text-gray-500'>Before: </span>
                            <span className='text-gray-600'>
                              {(item.oldData as {content?: string}).content}
                            </span>
                          </div>
                          <div>
                            <span className='text-xs font-medium text-gray-500'>After: </span>
                            <span className='text-gray-600'>
                              {(item.newData as {content?: string}).content}
                            </span>
                          </div>
                        </div>
                      )}
                    {item.action === ProspectAction.NOTE_DELETED && item.oldData && (
                      <div className='text-sm mb-2'>
                        <span className='text-xs font-medium text-gray-500'>Deleted note: </span>
                        <span className='text-gray-600'>
                          {(item.oldData as {content?: string}).content}
                        </span>
                      </div>
                    )}
                    <div className='flex items-center gap-2 text-xs text-gray-500'>
                      {item.changedByUser ? (
                        <>
                          <Avatar className='h-4 w-4'>
                            <AvatarFallback className='text-[8px]'>
                              {item.changedByUser.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .slice(0, 2)
                                .toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span>{item.changedByUser.name}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className='shrink-0 text-right'>
                    <div className='text-xs text-gray-500'>
                      {new Date(item.createdAt).toLocaleDateString()}
                    </div>
                    <div className='text-xs text-gray-400'>
                      {new Date(item.createdAt).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
